import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { BulkEditColumnDef } from './types';
import { abyssColors } from '@/theme/colors';

const SELECT_FIELD_TYPES = new Set(['SELECT', 'SINGLE_SELECT', 'MULTI_SELECT']);
const DATE_FIELD_TYPES = new Set(['DATE']);

type Props = {
  open: boolean;
  column: BulkEditColumnDef;
  lastValue: string;
  onApply: (value: string) => void;
  onClose: () => void;
};

const actionButtonSx = {
  fontSize: '1.2rem',
  fontWeight: 600,
  textTransform: 'none' as const,
  py: '4px',
  px: '14px',
  borderRadius: '4px',
  minWidth: 'unset',
  boxShadow: 'none',
  '&:hover': { boxShadow: 'none' },
};

export const BulkColumnEditPopover = ({
  open,
  column,
  lastValue,
  onApply,
  onClose,
}: Props) => {
  const [inputValue, setInputValue] = useState(lastValue);
  const options = column.options ?? [];

  const handleApply = () => {
    onApply(inputValue);
    setInputValue('');
    onClose();
  };

  const handleClose = () => {
    setInputValue('');
    onClose();
  };

  const renderInput = () => {
    if (SELECT_FIELD_TYPES.has(String(column.fieldType))) {
      return (
        <FormControl fullWidth size="small">
          <InputLabel sx={{ fontSize: '1.4rem', color: abyssColors.primary[300] }}>
            {column.label}
          </InputLabel>
          <Select
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            label={column.label}
            sx={{ fontSize: '1.4rem' }}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (DATE_FIELD_TYPES.has(String(column.fieldType))) {
      return (
        <TextField
          type="date"
          size="small"
          label={column.label}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
          sx={{ '& .MuiInputBase-root': { fontSize: '1.4rem' } }}
        />
      );
    }

    return (
      <TextField
        size="small"
        label={column.label}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        fullWidth
        sx={{ '& .MuiInputBase-root': { fontSize: '1.4rem' } }}
      />
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Typography
            sx={{
              fontSize: '1.6rem',
              fontWeight: 500,
              lineHeight: '24px',
              color: abyssColors.primary[500],
            }}
          >
            Bulk Edit {column.label}
          </Typography>
          <IconButton onClick={handleClose} size="small" sx={{ p: 0, mt: '2px' }}>
            <CloseIcon sx={{ fontSize: 16, color: abyssColors.primary[500] }} />
          </IconButton>
        </Stack>

        {renderInput()}

        <Stack direction="row" gap={2} justifyContent="flex-end">
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              ...actionButtonSx,
              borderColor: abyssColors.primary[500],
              color: abyssColors.primary[500],
              '&:hover': {
                borderColor: abyssColors.primary[500],
                bgcolor: 'transparent',
                boxShadow: 'none',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            sx={{
              ...actionButtonSx,
              bgcolor: abyssColors.primary[500],
              color: '#f6f8fb',
              '&:hover': { bgcolor: abyssColors.primary[400] },
            }}
          >
            Save
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
};
